<template>
    <div class="zs-dash-grid-container">
        <!-- Toolbar for grid controls -->
        <div v-if="showToolbar" class="zs-dash-grid-toolbar">
            <slot name="toolbar">
                <ZSButton v-if="allowAdd" size="small" color="primary" icon="plus" @click="addWidget">
                    Add Widget
                </ZSButton>
                <ZSButton v-if="allowSave" size="small" color="success" icon-size="small"  icon="save" @click="saveLayout">
                    Save Layout
                </ZSButton>
                <ZSButton v-if="allowClear" size="small" color="danger" icon-size="small" icon="trash" @click="clearGrid">
                    Clear
                </ZSButton>
                <ZSButton v-if="allowCompact" size="small" color="secondary" icon-size="small"  icon="compact" @click="compact">
                    Compact
                </ZSButton>
                <ZSButton :icon="isEditing ? 'Unlock' : 'Lock'" icon-size="small" size="small" :color="isEditing ? 'warning' : 'default'" @click="toggleEdit">
                    {{ isEditing ? 'Lock' : 'Edit' }}
                </ZSButton>
            </slot>
        </div>

        <!-- GridStack container -->
        <div ref="gridContainer" class="grid-stack"></div>

        <!-- Hidden slot container - widgets will be teleported from here -->
        <div ref="slotContainer" style="display: none;">
            <slot></slot>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, provide, nextTick, watch, useSlots } from 'vue'
import { GridStack } from 'gridstack'
import 'gridstack/dist/gridstack.min.css'

const props = defineProps({
    // Grid configuration
    columns: {
        type: Number,
        default: 12
    },
    cellHeight: {
        type: [Number, String],
        default: 80
    },
    minRow: {
        type: Number,
        default: 1
    },
    maxRow: {
        type: Number,
        default: 0 // 0 = no limit
    },
    margin: {
        type: [Number, String],
        default: 10
    },
    float: {
        type: Boolean,
        default: false
    },
    // Control options
    allowAdd: {
        type: Boolean,
        default: true
    },
    allowRemove: {
        type: Boolean,
        default: true
    },
    allowResize: {
        type: Boolean,
        default: true
    },
    allowMove: {
        type: Boolean,
        default: true
    },
    allowSave: {
        type: Boolean,
        default: true
    },
    allowClear: {
        type: Boolean,
        default: true
    },
    allowCompact: {
        type: Boolean,
        default: true
    },
    showToolbar: {
        type: Boolean,
        default: true
    },
    // Initial state
    editable: {
        type: Boolean,
        default: true
    },
    // Animation
    animate: {
        type: Boolean,
        default: true
    },
    // Layout data (for loading saved layouts)
    layout: {
        type: Array,
        default: () => []
    },
    // Removable settings
    removable: {
        type: [Boolean, String],
        default: false
    },
    // Accept external drag
    acceptWidgets: {
        type: [Boolean, String, Function],
        default: false
    }
})

const emit = defineEmits([
    'change',
    'added',
    'removed',
    'resize',
    'resizeStart',
    'resizeStop',
    'dragStart',
    'dragStop',
    'dropped',
    'save',
    'clear',
    'editToggle'
])

// Refs
const gridContainer = ref(null)
const slotContainer = ref(null)
let grid = null // DO NOT use ref() - GridStack doesn't work well with Vue proxies

// State
const isEditing = ref(props.editable)
const widgets = ref(new Map()) // Track widget instances

// Slots
const slots = useSlots()

// Initialize GridStack
const initGrid = () => {
    if (!gridContainer.value) return

    const options = {
        column: props.columns,
        cellHeight: props.cellHeight,
        minRow: props.minRow,
        maxRow: props.maxRow || undefined,
        margin: props.margin,
        float: props.float,
        animate: props.animate,
        disableResize: !props.allowResize,
        disableDrag: !props.allowMove,
        removable: props.removable,
        acceptWidgets: props.acceptWidgets,
        staticGrid: !isEditing.value
    }

    grid = GridStack.init(options, gridContainer.value)

    // Setup event handlers
    setupEventHandlers()

    // Process slot children
    processSlotChildren()

    // Load saved layout if provided
    if (props.layout.length > 0) {
        loadLayout(props.layout)
    }
}

// Setup GridStack event handlers
const setupEventHandlers = () => {
    if (!grid) return

    grid.on('change', (event, items) => {
        emit('change', items)
    })

    grid.on('added', (event, items) => {
        emit('added', items)
    })

    grid.on('removed', (event, items) => {
        items.forEach(item => {
            const widgetId = item.id
            if (widgets.value.has(widgetId)) {
                widgets.value.delete(widgetId)
            }
        })
        emit('removed', items)
    })

    grid.on('resizestart', (event, el) => {
        emit('resizeStart', el)
    })

    grid.on('resize', (event, el) => {
        emit('resize', el)
    })

    grid.on('resizestop', (event, el) => {
        emit('resizeStop', el)
    })

    grid.on('dragstart', (event, el) => {
        emit('dragStart', el)
    })

    grid.on('dragstop', (event, el) => {
        emit('dragStop', el)
    })

    grid.on('dropped', (event, previousNode, newNode) => {
        emit('dropped', { previousNode, newNode })
    })
}

// Process children from slot
const processSlotChildren = () => {
    if (!slotContainer.value || !grid) return

    nextTick(() => {
        const children = Array.from(slotContainer?.value?.children ?? [])
        
        children.forEach((child, index) => {
            // Get grid item options from data attributes or defaults
            const widgetOptions = {
                id: child.dataset.gsId || `widget-${Date.now()}-${index}`,
                x: parseInt(child.dataset.gsX) || undefined,
                y: parseInt(child.dataset.gsY) || undefined,
                w: parseInt(child.dataset.gsW) || 2,
                h: parseInt(child.dataset.gsH) || 2,
                minW: parseInt(child.dataset.gsMinW) || undefined,
                minH: parseInt(child.dataset.gsMinH) || undefined,
                maxW: parseInt(child.dataset.gsMaxW) || undefined,
                maxH: parseInt(child.dataset.gsMaxH) || undefined,
                noResize: child.dataset.gsNoResize === 'true',
                noMove: child.dataset.gsNoMove === 'true',
                locked: child.dataset.gsLocked === 'true',
                autoPosition: child.dataset.gsAutoPosition !== 'false'
            }

            // Create wrapper for the widget content
            const contentWrapper = document.createElement('div')
            contentWrapper.className = 'zs-dash-grid-widget-content'
            
            // Add remove button if allowed
            if (props.allowRemove) {
                const removeBtn = document.createElement('button')
                removeBtn.className = 'zs-dash-grid-remove-btn'
                removeBtn.innerHTML = '×'
                removeBtn.onclick = (e) => {
                    e.stopPropagation()
                    removeWidget(widgetOptions.id)
                }
                contentWrapper.appendChild(removeBtn)
            }

            // Move the slot child into the wrapper (not clone - preserves Vue reactivity)
            contentWrapper.appendChild(child)

            // Add widget to grid
            const widgetEl = grid.addWidget({
                ...widgetOptions,
                content: ''
            })

            // Replace content with our wrapper
            const itemContent = widgetEl.querySelector('.grid-stack-item-content')
            if (itemContent) {
                itemContent.innerHTML = ''
                itemContent.appendChild(contentWrapper)
            }

            // Track widget
            widgets.value.set(widgetOptions.id, {
                element: widgetEl,
                options: widgetOptions
            })
        })

        // Clear the hidden slot container
        if(slotContainer?.value?.innerHTML)slotContainer.value.innerHTML = ''
    })
}

// Add a new widget programmatically
const addWidget = (options = {}) => {
    if (!grid) return null

    const defaultOptions = {
        id: `widget-${Date.now()}`,
        x: undefined,
        y: undefined,
        w: 2,
        h: 2,
        autoPosition: true,
        content: options.content || '<div class="zs-dash-grid-widget-placeholder">New Widget</div>'
    }

    const widgetOptions = { ...defaultOptions, ...options }
    
    // Create content wrapper
    const contentWrapper = document.createElement('div')
    contentWrapper.className = 'zs-dash-grid-widget-content'
    
    if (props.allowRemove) {
        const removeBtn = document.createElement('button')
        removeBtn.className = 'zs-dash-grid-remove-btn'
        removeBtn.innerHTML = '×'
        removeBtn.onclick = (e) => {
            e.stopPropagation()
            removeWidget(widgetOptions.id)
        }
        contentWrapper.appendChild(removeBtn)
    }

    // Add the actual content
    const contentDiv = document.createElement('div')
    contentDiv.innerHTML = widgetOptions.content
    contentWrapper.appendChild(contentDiv)

    // Create widget
    const widgetEl = grid.addWidget({
        ...widgetOptions,
        content: ''
    })

    // Replace content
    const itemContent = widgetEl.querySelector('.grid-stack-item-content')
    if (itemContent) {
        itemContent.innerHTML = ''
        itemContent.appendChild(contentWrapper)
    }

    // Track widget
    widgets.value.set(widgetOptions.id, {
        element: widgetEl,
        options: widgetOptions
    })

    emit('added', [{ id: widgetOptions.id, el: widgetEl }])

    return widgetEl
}

// Add widget with Vue component
const addVueWidget = (component, componentProps = {}, gridOptions = {}) => {
    if (!grid) return null

    const widgetOptions = {
        id: `widget-${Date.now()}`,
        w: 2,
        h: 2,
        autoPosition: true,
        ...gridOptions
    }

    // Create widget element
    const widgetEl = grid.addWidget({
        ...widgetOptions,
        content: ''
    })

    // Get the content container
    const itemContent = widgetEl.querySelector('.grid-stack-item-content')
    if (itemContent) {
        // Create wrapper
        const contentWrapper = document.createElement('div')
        contentWrapper.className = 'zs-dash-grid-widget-content'

        if (props.allowRemove) {
            const removeBtn = document.createElement('button')
            removeBtn.className = 'zs-dash-grid-remove-btn'
            removeBtn.innerHTML = '×'
            removeBtn.onclick = (e) => {
                e.stopPropagation()
                removeWidget(widgetOptions.id)
            }
            contentWrapper.appendChild(removeBtn)
        }

        // Create mount point for Vue component
        const mountPoint = document.createElement('div')
        mountPoint.className = 'zs-dash-grid-vue-mount'
        contentWrapper.appendChild(mountPoint)

        itemContent.innerHTML = ''
        itemContent.appendChild(contentWrapper)

        // Store reference for external mounting
        widgets.value.set(widgetOptions.id, {
            element: widgetEl,
            options: widgetOptions,
            mountPoint,
            component,
            componentProps
        })
    }

    return { widgetEl, widgetId: widgetOptions.id }
}

// Remove a widget
const removeWidget = (id) => {
    if (!grid) return

    const widget = widgets.value.get(id)
    if (widget && widget.element) {
        grid.removeWidget(widget.element)
        widgets.value.delete(id)
    }
}

// Update widget
const updateWidget = (id, options) => {
    if (!grid) return

    const widget = widgets.value.get(id)
    if (widget && widget.element) {
        grid.update(widget.element, options)
        widget.options = { ...widget.options, ...options }
    }
}

// Save current layout
const saveLayout = () => {
    if (!grid) return []

    const layout = grid.save(false) // false = don't include content
    emit('save', layout)
    return layout
}

// Load layout
const loadLayout = (layout) => {
    if (!grid || !layout) return

    grid.load(layout)
}

// Clear all widgets
const clearGrid = () => {
    if (!grid) return

    grid.removeAll()
    widgets.value.clear()
    emit('clear')
}

// Compact the grid
const compact = () => {
    if (!grid) return

    grid.compact()
}

// Toggle edit mode
const toggleEdit = () => {
    isEditing.value = !isEditing.value
    
    if (grid) {
        grid.setStatic(!isEditing.value)
    }
    
    emit('editToggle', isEditing.value)
}

// Enable/disable grid editing
const setEditable = (editable) => {
    isEditing.value = editable
    
    if (grid) {
        grid.setStatic(!editable)
    }
}

// Get widget by ID
const getWidget = (id) => {
    return widgets.value.get(id)
}

// Get all widgets
const getAllWidgets = () => {
    return Array.from(widgets.value.values())
}

// Get grid instance (for advanced usage)
const getGridInstance = () => {
    return grid
}

// Make a DOM element a widget
const makeWidget = (el, options = {}) => {
    if (!grid) return null

    return grid.makeWidget(el, options)
}

// Batch update
const batchUpdate = (callback) => {
    if (!grid) return

    grid.batchUpdate()
    callback()
    grid.commit()
}

// Watch for layout prop changes
watch(() => props.layout, (newLayout) => {
    if (newLayout && newLayout.length > 0 && grid) {
        loadLayout(newLayout)
    }
}, { deep: true })

// Watch for editable prop changes
watch(() => props.editable, (newEditable) => {
    setEditable(newEditable)
})

// Provide grid methods to child components
provide('dashGrid', {
    addWidget,
    addVueWidget,
    removeWidget,
    updateWidget,
    saveLayout,
    loadLayout,
    clearGrid,
    compact,
    toggleEdit,
    setEditable,
    getWidget,
    getAllWidgets,
    getGridInstance,
    makeWidget,
    batchUpdate
})

// Lifecycle
onMounted(() => {
    initGrid()
})

onBeforeUnmount(() => {
    if (grid) {
        grid.destroy()
        grid = null
    }
    widgets.value.clear()
})

// Expose methods
defineExpose({
    addWidget,
    addVueWidget,
    removeWidget,
    updateWidget,
    saveLayout,
    loadLayout,
    clearGrid,
    compact,
    toggleEdit,
    setEditable,
    getWidget,
    getAllWidgets,
    getGridInstance,
    makeWidget,
    batchUpdate,
    isEditing
})
</script>

<style>
:root {
    --zs-dash-grid-bg: #f5f5f5;
    --zs-dash-grid-gap: 10px;
    --zs-dash-grid-min-height: 400px;
    --zs-dash-grid-toolbar-gap: 10px;
    --zs-dash-grid-toolbar-padding: 10px 0;
    --zs-dash-grid-widget-bg: #ffffff;
    --zs-dash-grid-widget-border: 1px solid #e0e0e0;
    --zs-dash-grid-widget-border-radius: 8px;
    --zs-dash-grid-widget-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    --zs-dash-grid-widget-padding: 15px;
    --zs-dash-grid-remove-btn-size: 24px;
    --zs-dash-grid-remove-btn-bg: #ff5252;
    --zs-dash-grid-remove-btn-color: #ffffff;
    --zs-dash-grid-placeholder-bg: rgba(0, 123, 255, 0.1);
    --zs-dash-grid-placeholder-border: 2px dashed #007bff;
}

.zs-dash-grid-container {
    width: 100%;
}

.zs-dash-grid-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: var(--zs-dash-grid-toolbar-gap);
    padding: var(--zs-dash-grid-toolbar-padding);
    margin-bottom: 10px;
    align-items: center;
}

/* GridStack base styling */
.zs-dash-grid-container .grid-stack {
    background: var(--zs-dash-grid-bg);
    min-height: var(--zs-dash-grid-min-height);
    border-radius: 8px;
}

/* Widget item styling */
.zs-dash-grid-container .grid-stack-item-content {
    background: var(--zs-dash-grid-widget-bg);
    border: var(--zs-dash-grid-widget-border);
    border-radius: var(--zs-dash-grid-widget-border-radius);
    box-shadow: var(--zs-dash-grid-widget-shadow);
    overflow: hidden;
}

.zs-dash-grid-widget-content {
    position: relative;
    width: 100%;
    height: 100%;
    padding: var(--zs-dash-grid-widget-padding);
    box-sizing: border-box;
    overflow: auto;
}

/* Remove button */
.zs-dash-grid-remove-btn {
    position: absolute;
    top: 5px;
    right: 5px;
    width: var(--zs-dash-grid-remove-btn-size);
    height: var(--zs-dash-grid-remove-btn-size);
    border-radius: 50%;
    background: var(--zs-dash-grid-remove-btn-bg);
    color: var(--zs-dash-grid-remove-btn-color);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: bold;
    line-height: 1;
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: 10;
}

.grid-stack-item:hover .zs-dash-grid-remove-btn {
    opacity: 1;
}

.zs-dash-grid-remove-btn:hover {
    background: #ff1744;
    transform: scale(1.1);
}

/* Placeholder for new widgets */
.zs-dash-grid-widget-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #999;
    font-size: 14px;
}

/* Vue mount point */
.zs-dash-grid-vue-mount {
    width: 100%;
    height: 100%;
}

/* GridStack placeholder (during drag) */
.zs-dash-grid-container .grid-stack-placeholder > .placeholder-content {
    background: var(--zs-dash-grid-placeholder-bg);
    border: var(--zs-dash-grid-placeholder-border);
    border-radius: var(--zs-dash-grid-widget-border-radius);
}

/* Resize handles styling */
.zs-dash-grid-container .grid-stack-item > .ui-resizable-handle {
    background: transparent;
}

.zs-dash-grid-container .grid-stack-item > .ui-resizable-se {
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10'%3E%3Cpath d='M0 10L10 0M0 6l6-6M0 2l2-2' stroke='%23999' stroke-width='1'/%3E%3C/svg%3E") no-repeat center;
    background-size: 10px;
    width: 20px;
    height: 20px;
    right: 2px;
    bottom: 2px;
}

/* Static mode (non-editable) */
.zs-dash-grid-container .grid-stack-static .grid-stack-item-content {
    cursor: default;
}

.zs-dash-grid-container .grid-stack-static .zs-dash-grid-remove-btn {
    display: none;
}

/* Animation */
.zs-dash-grid-container .grid-stack.grid-stack-animate .grid-stack-item {
    transition: left 0.3s, top 0.3s, width 0.3s, height 0.3s;
}

/* Responsive columns */
@media (max-width: 768px) {
    .zs-dash-grid-container .grid-stack {
        --gs-column: 1;
    }
}
</style>
